import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import type { AuditClock, AuditListData, Envelope } from '../api/types.js';
import { AuditList } from '../components/Audit/AuditList.js';
import { AuditBody as Body, AuditFrame } from '../components/Audit/AuditPage.js';
import { AuditResults } from '../components/Audit/AuditResults.js';
import { AuditRuns } from '../components/Audit/AuditRuns.js';
import { AuditListSkeleton, AuditResultsSkeleton, AuditRunsSkeleton } from '../components/Audit/AuditSkeleton.js';
import { formatRunDate } from '../components/Audit/shared.js';
import { queries } from '../store/queries.js';
import { useQuery, type QueryResult } from '../store/StoreProvider.js';

/**
 * The Audit page — one route component, three states (spec 007, PRD-Audit §6.2):
 * `/audit` (List) · `/audit/:auditId` (Runs) · `/audit/:auditId/:runDate` (Results).
 *
 * Three rules this component owns:
 *
 * - **The header shows the AUDIT's clock, never the snapshot's as-of** (FR-AU28, constitution
 *   3.0.0 gate 7). It is the List's clock — the newest run across the daily audits — which the
 *   store prefetched at sign-in, so every state shows the same date; a deep link that lands before
 *   the List does shows the state's own clock until it arrives (`Page`).
 * - **The staleness word waits for a fresh check** (AU-19, FR-AU30). A List copy is fetched again
 *   once a newer run has become due — 07:30 New York time on the next weekday (`staleWhen`, the
 *   server's `clock.behindAt`); the word shows only when the server still says `stale` and nothing
 *   is in flight — so an old browser copy is never reported as a stale audit.
 * - **A kept copy survives an outage** (FR-AU37). When a refresh fails but a copy is held, the
 *   copy stays on screen with when it was fetched; with nothing held, the error banner and Retry.
 *   A table is never blank or zero-filled.
 *
 * The frame and the states live in `components/Audit/AuditPage.tsx` since spec 008, because Alerts
 * shows the same audit data and must state its clock and its failures identically.
 */

const CRUMB = 'CYNET Health Commercial';

function RunsState({ auditId, list }: { auditId: string; list: ListState }) {
  const state = useQuery(queries.auditRuns(auditId));
  const own = state.data?.data ?? null;
  return (
    <Page list={list} auditId={auditId} own={own ? { name: own.audit.name, clock: own.clock } : null} ownLoading={state.loading}>
      <Body state={state} render={(data) => <AuditRuns data={data} />} skeleton={<AuditRunsSkeleton />} />
    </Page>
  );
}

function ResultsState({ auditId, runDate, list }: { auditId: string; runDate: string; list: ListState }) {
  const state = useQuery(queries.auditResults(auditId, runDate));
  const own = state.data?.data ?? null;
  return (
    <Page
      list={list}
      auditId={auditId}
      runDate={runDate}
      own={own ? { name: own.audit.name, clock: own.clock } : null}
      ownLoading={state.loading}
    >
      <Body
        state={state}
        render={(data, envelope) => <AuditResults data={data} envelope={envelope} />}
        skeleton={<AuditResultsSkeleton />}
      />
    </Page>
  );
}

type ListState = QueryResult<Envelope<AuditListData>>;

/**
 * Header + body frame. `own` is the state's OWN response (Runs/Results), used only while the List
 * has not landed — a deep link opened in a fresh browser, where the uncached List can take ~14 s
 * of run discovery (PRD-Audit §8.9). Without it the header read "No data yet" and the title the
 * raw audit id for that whole time, though the page body beneath had already rendered.
 */
function Page({
  list,
  auditId,
  runDate,
  own,
  ownLoading = false,
  children,
}: {
  list: ListState;
  auditId?: string;
  runDate?: string;
  own: { name: string; clock: AuditClock } | null;
  ownLoading?: boolean;
  children: ReactNode;
}) {
  const clock = list.data?.data?.clock ?? own?.clock ?? null;
  // Nothing has answered yet: no "No data yet" in the header, and no raw id as the title — the id
  // is kept only once a response has come back without a name (an unknown audit's own state).
  const pending = clock === null && (list.loading || ownLoading);
  const name =
    list.data?.data?.sections.flatMap((s) => s.audits).find((a) => a.id === auditId)?.name ??
    own?.name ??
    (pending ? null : auditId) ??
    null;

  const crumb = runDate && name ? `${CRUMB} · Audit · ${name}` : auditId ? `${CRUMB} · Audit` : CRUMB;
  const title = runDate ? `Run — ${formatRunDate(runDate)}` : auditId ? (name ?? 'Audit') : 'Audit';

  return (
    <AuditFrame
      crumb={crumb}
      title={title}
      envelope={list.data}
      clock={clock}
      revalidating={list.revalidating}
      pending={pending}
    >
      {children}
    </AuditFrame>
  );
}

export function Audit() {
  const { auditId, runDate } = useParams();
  const list = useQuery(queries.auditList());

  if (auditId && runDate) return <ResultsState auditId={auditId} runDate={runDate} list={list} />;
  if (auditId) return <RunsState auditId={auditId} list={list} />;
  return (
    <Page list={list} own={null}>
      <Body state={list} render={(data) => <AuditList data={data} />} skeleton={<AuditListSkeleton />} />
    </Page>
  );
}
